import React from 'react'
import { RealEstateCategory, RealEstateFeatures, SegmentationType, TransactionType } from '../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../@types/flow-types'
import { PROPERTY_OWNER_STORE_SELL_FLOW_PATH } from '../../../../../services/routes/routes'
import { propertyOwnerStoreSellSchema } from './propertyOwnerStoreSellSchema'
import {
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONNECTIONS,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OTHER_FACILITIES,
  SCREEN_REALTOR_LEAD_ENGINE,
  SCREENS,
} from '../../../../../services/domain/wizardScreens'
import ContactInfo from '../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Address from '../../../../../components/pages/insertionPage/screens/address/Address'
import Construction from '../../../../../components/pages/insertionPage/screens/construction/Construction'

import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import Description from '../../../../../components/pages/insertionPage/screens/description/Description'
import OtherFacilities from '../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import {
  estateFeatures,
  flooringType,
  floorLoadField,
  freeFrom,
  frontWindowLength,
  goodsLift,
  interiorQuality,
  locationClassificationType,
  parkingSpace,
  supplyType,
  totalFloorsField,
} from '../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import BasicInfo from '../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  mainFloorSpaceField,
  priceField,
  purchasePriceOnRequestToggle,
  separableSpaceField,
  totalFloorSpaceField,
} from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { PriceFieldType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { MainFloorType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/mainFloorSpaceField/MainFloorSpaceField'
import { SeparableSpaceType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/separableSpaceField/SeparableSpaceField'
import { buildingTypeField } from '../../../../../components/pages/insertionPage/screens/commonFields/buildingType/BuildingType'
import { BuildingTypeOption } from '../../../../../@types/dropdown-types'
import { priceToApiSell } from '../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import Connections from '../../../../../components/pages/insertionPage/screens/connections/Connections'
import { RealtorLeadEngine } from '../../../../../components/pages/insertionPage/screens/realtorLeadEngine/RealtorLeadEngine'
import Multimedia from '../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

const propertyOwnerStoreSellFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        screenTooltipKey='tooltip.screens.basicInfo.storeRentSell'
        properties={[
          mainFloorSpaceField(MainFloorType.STORE_AREA, 'left', undefined),
          separableSpaceField(SeparableSpaceType.STORE_AREA, 'right', undefined),
          totalFloorSpaceField(),
          buildingTypeField(
            'object',
            [
              BuildingTypeOption.SHOWROOM_SPACE,
              BuildingTypeOption.SHOPPING_CENTRE,
              BuildingTypeOption.FACTORY_OUTLET,
              BuildingTypeOption.DEPARTMENT_STORE,
              BuildingTypeOption.KIOSK,
              BuildingTypeOption.SELF_SERVICE_MARKET,
              BuildingTypeOption.SALES_AREA,
              BuildingTypeOption.SALES_HALL,
            ],
            getDefaultClasses('right')
          ),
          priceField(PriceFieldType.BUY_PRICE, getDefaultClasses('left')),
          freeFrom(getDefaultClasses('right')),
          purchasePriceOnRequestToggle(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasFurnishingNote={true} hasLocationNote={true} hasOtherNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        {...props}
        screenTooltipKey='tooltip.screens.otherFacilities.allSell'
        properties={[
          estateFeatures([
            RealEstateFeatures.BASEMENT,
            RealEstateFeatures.RAMP,
            RealEstateFeatures.LISTED,
            RealEstateFeatures.ELEVATOR,
          ]),
          interiorQuality(),
          locationClassificationType(),
          frontWindowLength(getDefaultClasses('right')),
          supplyType(),
          flooringType(),
          floorLoadField(getDefaultClasses('left'), 'floorLoadCeiling'),
          totalFloorsField(getDefaultClasses('right')),
          goodsLift(),
          parkingSpace({ showParkingSpaceType: false }),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONNECTIONS],
    element: (props) => <Connections {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <Construction {...props} hasLastModernizationField={true} />,
  },
  {
    screen: SCREENS[SCREEN_REALTOR_LEAD_ENGINE],
    element: (props) => <RealtorLeadEngine {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
]

export const propertyOwnerStoreSellFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.owner',
    estateType: 'wizard.navigation.header.store',
    offerType: 'wizard.navigation.header.sells',
    path: PROPERTY_OWNER_STORE_SELL_FLOW_PATH,
    name: FlowName.PROPERTY_OWNER_STORE_SELL,
    validationSchema: propertyOwnerStoreSellSchema,
    priceToApiMapper: priceToApiSell,
    metaData: {
      category: RealEstateCategory.STORE,
      segmentation: SegmentationType.PROPERTY_OWNER,
      transaction: TransactionType.SELL,
    },
  },
  defaultStateValues: { hideStreetAndStreetNumber: false },
  flowScreens: propertyOwnerStoreSellFlowScreens,
}
